import React, { useMemo, useRef, useEffect, useCallback } from 'react';
import { useLocale } from '@hooks/useLocale';
import { useTranslation } from 'next-i18next';
import cn from 'classnames';

import { titleCase } from '@utils/plp/utils/transforms';

import {
  isPrimaryProductImage,
  MecColour,
  MecImage,
  MecProduct,
  PriceType,
} from '@adapters/bigcommerce/types/product';
import { useCustomer } from '@adapters/bigcommerce/hooks/customer';
import { usePdp } from '@context/PdpContext';
import { trackProductEvent, GA4_EVENT_NAMES } from '@utils/gtm/ga4';
import { getGenderFromString } from '@utils/gtm/ga4/utils';
import { albyTrackLoadPDP } from '@utils/alby';
import { FEATURE_FLAG_ALBY_CHATBOT } from '@config/feature-flag';
import { createDealsBannerTrackingText } from '@utils/gtm/ga4/utils';
import { FIRST_IMAGE_SIZES } from '_components/ImageGallery/constants';

import styles from './ColourPicker.module.scss';
import { ColourPicker } from './ColourPicker';
import type { ColourPickerColour } from './ColourPickerContext';
import { usePdpSelectionFromQuery } from './usePdpSelectionFromQuery';

export type ColourPickerPlacement = 'pdp' | 'card';

interface PdpColourPickerV2Props {
  path: string;
  product: MecProduct;
  placement?: ColourPickerPlacement;
  prismicDealsBannerTitles?: string[];
}

const { VIEW_ITEM } = GA4_EVENT_NAMES;

type PdpColour = MecColour & { notAvailableForSelectedSize?: boolean };

/**
 * First hero (PRI) gallery image matching this colour, falling back to its
 * first image of any type. This is the image the gallery will actually show
 * once the colour is selected, so it's what hover-preload should warm.
 */
const getColourHeroImage = (
  images: MecImage[] | undefined,
  colourName: string
): string | null => {
  const matching = images?.filter((img) => img.swatchMatch === colourName);
  if (!matching?.length) return null;
  const hero = matching.find(isPrimaryProductImage) ?? matching[0];
  return hero.urlOriginal ?? null;
};

const mapColoursToViewModel = (
  colours: PdpColour[] | undefined,
  selectedColourId: number | null,
  images: MecImage[] | undefined
): ColourPickerColour[] => {
  if (!colours?.length) return [];

  return colours.map((colour) => ({
    id: colour.id,
    name: colour.name,
    swatchImage: colour.swatchImage,
    productImage: getColourHeroImage(images, colour.name),
    hexColours: colour.hexColours,
    isSelected: colour.id === selectedColourId,
    isDisabled: !!colour.notAvailableForSelectedSize,
    isDeal:
      colour.price?.priceType === PriceType.Sale ||
      colour.price?.priceType === PriceType.Clearance,
  }));
};

const PdpColourPickerV2: React.FC<PdpColourPickerV2Props> = ({
  path,
  product,
  placement = 'pdp',
  prismicDealsBannerTitles = [],
}) => {
  const { t } = useTranslation(['pdp', 'common']);
  const { customerIsLoggedIn, customerAuthReady } = useCustomer();
  const locale = useLocale();
  const {
    colours,
    selectedColour,
    selectedColourId,
    setSelectedColourId,
    setSelectedSizeId,
    isGiftCard,
    promoBadge,
    price,
    hasComparison,
    hasFranchiseComparison,
    hasMemberPriceDifference,
  } = usePdp();
  const firstFiredFlags = useRef<Array<'tracking'>>([]);
  const pendingUrlSync = useRef<{ frame?: number; timeout?: number }>({});

  const cancelPendingUrlSync = useCallback(() => {
    const { frame, timeout } = pendingUrlSync.current;
    if (frame !== undefined) cancelAnimationFrame(frame);
    if (timeout !== undefined) window.clearTimeout(timeout);
    pendingUrlSync.current = {};
  }, []);

  useEffect(() => cancelPendingUrlSync, [cancelPendingUrlSync]);

  usePdpSelectionFromQuery({ path, product });

  const viewModelColours = useMemo(
    () => mapColoursToViewModel(colours, selectedColourId, product.images),
    [colours, selectedColourId, product.images]
  );

  const updatedProduct = useCallback(() => {
    return {
      ...product,
      gender: getGenderFromString(product?.name),
      selectedColour: selectedColour?.name,
      promoBadge: promoBadge ?? undefined,
      selectedPrice: price
        ? {
            basePrice: price.basePrice,
            price: price.price,
          }
        : undefined,
      hasComparison: hasComparison,
      hasFranchiseComparison: hasFranchiseComparison,
      hasMemberPriceDifference,
      dealsBanner: createDealsBannerTrackingText({
        priceType: price?.priceType ?? PriceType.Regular,
        t,
        prismicBannerTitles: prismicDealsBannerTitles,
        memberPriceLabel: hasMemberPriceDifference
          ? t(
              customerIsLoggedIn
                ? 'common:product.info.banner.membership.discount.title.loggedIn'
                : 'common:product.info.banner.membership.discount.title.loggedOut'
            )
          : undefined,
      }),
    };
  }, [
    product,
    selectedColour?.name,
    promoBadge,
    price,
    hasComparison,
    hasFranchiseComparison,
    hasMemberPriceDifference,
    customerIsLoggedIn,
    prismicDealsBannerTitles,
    t,
  ]);

  useEffect(() => {
    if (
      selectedColour?.name &&
      customerAuthReady &&
      !firstFiredFlags.current.includes('tracking')
    ) {
      const updatedProductData = updatedProduct();
      trackProductEvent({
        eventName: VIEW_ITEM,
        products: [updatedProductData],
      });
      if (
        updatedProductData?.code &&
        locale === 'en' &&
        FEATURE_FLAG_ALBY_CHATBOT
      ) {
        albyTrackLoadPDP(updatedProductData?.code);
      }
      firstFiredFlags.current.push('tracking');
    }
  }, [selectedColour, updatedProduct, locale, customerAuthReady]);

  const handleSelectColour = useCallback(
    (colourId: number) => {
      const selected = viewModelColours.find((c) => c.id === colourId);
      if (selected?.isDisabled) {
        setSelectedSizeId(null);
      }
      setSelectedColourId(colourId);

      // Analytics scripts patch history.replaceState and scan the DOM on every
      // URL change; syncing the URL after the next paint keeps that work out of
      // the click's interaction latency. Only the latest click's sync runs, so
      // rapid taps don't each pay for that scan.
      cancelPendingUrlSync();
      pendingUrlSync.current.frame = requestAnimationFrame(() => {
        pendingUrlSync.current.timeout = window.setTimeout(() => {
          pendingUrlSync.current = {};
          const params = new URLSearchParams(window.location.search);

          if (selected?.name) {
            params.set('colour', selected.name);
          } else {
            params.delete('colour');
          }

          const search = params.toString();
          const pathname = window.location.pathname;
          const nextUrl = search ? `${pathname}?${search}` : pathname;

          window.history.replaceState(null, '', nextUrl);
        }, 0);
      });
    },
    [
      viewModelColours,
      setSelectedColourId,
      setSelectedSizeId,
      cancelPendingUrlSync,
    ]
  );

  const contextValue = useMemo(
    () => ({
      state: { colours: viewModelColours },
      actions: { onSelectColour: handleSelectColour },
      variant: placement,
      // Regardless of `placement` (a layout variant only), a selected colour here
      // always swaps the PDP gallery's hero slide, so hover-preload must target
      // that slide's `sizes`, not the swatch's own on-screen size.
      preloadImageSizes: FIRST_IMAGE_SIZES,
    }),
    [viewModelColours, handleSelectColour, placement]
  );

  if (
    !viewModelColours.length ||
    (viewModelColours.length === 1 && viewModelColours[0].name === 'NO_COLOUR')
  ) {
    return null;
  }

  const labelText = isGiftCard
    ? t('product.giftcard.card.design')
    : t('product.colour');

  return (
    <div
      className={cn(styles.root, {
        [styles.rootCard]: placement === 'card',
      })}
      data-testid="colour-picker-root"
      data-placement={placement}
    >
      <div className={styles.container}>
        <div className={styles.labelRow}>
          <span className={styles.label}>{labelText}</span>
          <span
            className={styles.selectedColourName}
            data-testid="selected-colour-name"
          >
            {selectedColour?.name ? titleCase(selectedColour.name) : null}
          </span>
        </div>
        <div className={styles.swatchContainer}>
          <ColourPicker.Provider value={contextValue}>
            <div role="radiogroup" aria-label={labelText}>
              <ColourPicker.Grid />
            </div>
          </ColourPicker.Provider>
        </div>
      </div>
    </div>
  );
};

export default PdpColourPickerV2;
